import React from 'react';
import { faDatabase } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/new-era-information.jpg
import newEraInfoImg from '../../../assets/images/blogs/New-Era-Information-Systems.png';

const NewEraInformationRepresentation = () => (
    <BlogPost
        title="A New Era of Information Representation with AI"
        subtitle="We aren't replacing human knowledge and expertise - we're relocating where it matters most."
        category="Cognition"
        categoryIcon={faDatabase}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={newEraInfoImg}
        imageAlt="A new era of information representation - AI-native semantic systems"
        ctaHeading="Want to see AI-native information extraction?"
        ctaText="Book a discovery call and we'll show you how our system achieves 80-90% accuracy in minutes, not months."
        seoDescription="How decades of information theory experience led to building AI systems that achieve 96% vocabulary coverage and 80% accuracy with zero manual curation."
        seoKeywords="information representation, AI semantics, knowledge extraction, clinical AI, ConsistentAI, Nebulyx"
        slug="new-era-information-representation"
    >
        <p>
            One of my first solutions (90s) was a flight information system for Airports -
            making sure the passenger boards were up to date and correct.
        </p>

        <p>
            This taught me some of the basics of information theory.
        </p>

        <p>
            The 2010s brought in Big Data - I was in finance, doing a lot of Big Data
            information systems for Risk/Finance &amp; Trading. Taught me about scale +
            started to work with unstructured data (documents) which exposed me to semantic
            (and human) representations of information.
        </p>

        <p>
            I built my first graph engine here too, and have been building graphs
            (Knowledge/Execution/Hybrid) ever since.
        </p>

        <h3>The Journey to AI-Native Semantics</h3>

        <p>
            2016/2017: I led a world-class AI team to build an AI-based information retrieval
            system, directly from deep domain documentation. No transformers, classic ML and
            deep domain taxonomies hand-crafted by domain SMEs.
        </p>

        <p>
            This taught me a lot about deep information theory including Natural Language
            Processing/Understanding &amp; Generation. It led me to build a more generic
            semantic inference solution, but had limits of the tech at the time.
        </p>

        <p>
            Since founding Nebulyx AI Ltd (7+ years), I've been doing many AI (LLM-based)
            use-cases directly from human language sources.
        </p>

        <p>
            My current use-case needs dynamic curation and inference but with deep semantics
            at its core. Traditional AI (LLMs) and Info/Knowledge Management approaches didn't
            work (I tried), so I built a solution (new LLM design → Pat. Pend.) from
            scratch to solve this, using all my experience.
        </p>

        <h3>Testing It For Real</h3>

        <p>
            I've seen some scepticism around AI-based semantic systems, so I tested it.
        </p>

        <p>
            One test used NICE NG109, clinical guideline for lower urinary tract infection
            prescribing. 41 pages: recommendations, dosages, contraindications, patient
            pathways.
        </p>

        <p>
            Fed it into my system - no manual curation - and ran tests. I had never worked
            with this document before, have no medical background, and did no domain
            preparation.
        </p>

        <h3>The Results</h3>

        <p>
            ✅ <strong>Vocabulary coverage: 96%</strong> - clinical concepts extracted
            automatically from the PDF.
        </p>

        <p>
            ✅ <strong>Contraindication accuracy: 80%</strong> - safety-critical queries
            answered correctly. Nitrofurantoin and renal thresholds. Pregnancy cautions.
            Prostate considerations.
        </p>

        <p>
            ✅ <strong>Provenance traceability: 100%</strong> - every result linked back
            to source. With page, section, citation provenance.
        </p>

        <p>
            <strong>Time to load and train: under 2 minutes on my laptop.</strong>
        </p>

        <p>
            Then I sat back and thought: this actually works.
        </p>

        <h3>What I Learned</h3>

        <p>
            The 80% is honest. Edge cases need tuning - e.g. footnotes need better weighting,
            query expansion, etc.
        </p>

        <p>
            But most of all, we can do dynamic ingestion with domain SMEs quickly and easily
            able to fine-tune directly.
        </p>

        <blockquote className="blog-post-quote">
            "The system got me 80-90% of the way in 2 minutes whilst allowing SMEs to verify
            and address quickly and easily. For my use-case the SME expertise now goes into
            the last 20%, not the first 80%."
        </blockquote>

        <p>
            We aren't replacing human knowledge and knowledge management expertise - we're
            relocating where it matters most. 🎯
        </p>
    </BlogPost>
);

export default NewEraInformationRepresentation;
